import { Injector } from '@angular/core';
import { Router } from '@angular/router';
import { CardsSuccessCodes } from '@app/translations';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  ActionButtonOutcomesType,
  BaseWidgetProperties,
  ConfirmationClaimType,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { take } from 'rxjs';
import { CardActivateAction } from '../../../actions/card-management.action';
import { ConfirmationService } from '../../../services/confirmation-service/confirmation-service';
import { AccountState } from '../../../state/account.state';
import { CardState } from '../../../state/card.state';
import { findAndShowAlertFromCode } from '../../alert-code-utils/alert-code.utils';
import { getQueryParamValueByKey } from '../../query-params.utils';
import { handleRedirection } from '../cta-redirection.utils';

/**
 * Activates the card currently being viewed against the account currently being
 * viewed. Activation always requires confirmation: the user is routed through
 * the confirmation flow and the card is only activated once confirmation
 * completes. Navigates to the success or failure outcome based on the API
 * response.
 *
 * @param injector - The Angular Injector used to retrieve services.
 * @param widgetProperties - The widget properties used to resolve redirection targets.
 */
export function onActivateCard(injector: Injector, widgetProperties: BaseWidgetProperties): void {
  const store = injector.get(Store);
  const alertHandlerService = injector.get(AlertHandlerService);
  const cardId = store.selectSnapshot(CardState.getCurrentCardId);
  const accountId = store.selectSnapshot(AccountState.getCurrentAccountId);

  if (!cardId) {
    alertHandlerService.showAlertFn('error', 'Card information is missing.');
    return;
  }

  const router = injector.get(Router);

  const activateCard = (accessToken?: string) =>
    store
      .dispatch(new CardActivateAction({ accountId, cardId }, accessToken))
      .pipe(take(1))
      .subscribe({
        next: () => {
          findAndShowAlertFromCode(store, router, alertHandlerService, [CardsSuccessCodes.CARD_ACTIVATED]);
          handleRedirection(widgetProperties, injector, ActionButtonOutcomesType.SUCCESS, true, false);
        },
        error: (error) => {
          console.error('Error activating card:', error);
          handleRedirection(widgetProperties, injector, ActionButtonOutcomesType.FAILURE, true, false);
        },
      });

  const confirmationService = injector.get(ConfirmationService);
  // Strip query params so the returning URL still matches once confirmation completes.
  const returnUrl = router.url.split('?')[0];

  confirmationService
    .checkConfirmation(returnUrl)
    .pipe(take(1))
    .subscribe(() => {
      activateCard(getQueryParamValueByKey('confirmation'));
    });

  confirmationService.confirmAction(ConfirmationClaimType.ACTIVATE_CARD, returnUrl, true);
}
